/**
 * Client-side derivation of the industry-level `AccountInsights` shown in
 * `AccountInsightsBar` above the Account View bubble chart. Ported verbatim
 * from `UcPipelineService.computeInsights` (Java, the deprecated GetAccountCrossUc
 * op). Moved to the portal so we can retire that op AND so PMs can tune the
 * five thresholds without a service deploy — the cutoffs are pure display
 * heuristics, not data-model invariants.
 *
 * Semantics for each callout (matches the Java exactly):
 *   - `highArrLowInfluence`: ARR ≥ $1M AND density < 1 (whales we're
 *     under-engaging).
 *   - `overInvested`: density ≥ 5 AND ARR < $200K (small accounts we're
 *     over-investing in relative to their revenue potential).
 *   - `staleHighValue`: freshness === 'stale' AND ARR ≥ $100K (high-value
 *     accounts that haven't been touched recently).
 *   - `healthyCount`: freshness === 'active' AND density ≥ 2.
 *
 * Each list is capped at 5, matching the Java `INSIGHT_LIMIT`.
 *
 * Reads only the per-account fields that ACCOUNT-grain PipelineBubbles carry —
 * accountName + totalArr + influenceDensity + freshness + bestDaysSince. Any
 * bubble missing accountName is skipped (would render as blank in the callout).
 */

import type { AccountBubble } from '../pages/UseCasesPage/components/PipelineMap';

/**
 * These three shapes were the output of `GetAccountCrossUc`, and were imported from the
 * generated client. That op is retired and nothing calls it, so importing its shapes kept
 * the frontend coupled to a contract the backend wants to delete — deleting the op would
 * have dropped the shapes from the regenerated client and failed this build.
 *
 * Owning them here is also the honest home: these insights are DERIVED client-side by
 * `deriveAccountInsights` below, so the frontend, not the service, defines them. Fields stay
 * optional to match what the generated shapes declared, so no consumer had to change.
 */
export interface InsightAccount {
  name: string | undefined;
  arr: number | undefined;
  density: number | undefined;
}

export interface StaleInsightAccount {
  name: string | undefined;
  arr: number | undefined;
  days?: number | undefined;
}

export interface AccountInsights {
  totalAccounts: number | undefined;
  /** ARR >= $1M but influence density < 1 (under-engaged whales). */
  highArrLowInfluence: InsightAccount[] | undefined;
  /** Influence density >= 5 but ARR < $200K (over-invested). */
  overInvested: InsightAccount[] | undefined;
  /** Stale (no recent activity) with ARR >= $100K. */
  staleHighValue: StaleInsightAccount[] | undefined;
  /** Count of healthy accounts (active + density >= 2). */
  healthyCount: number | undefined;
}

const WHALE_ARR = 1_000_000;
const LOW_INFLUENCE = 1;
const OVER_INVESTED_DENSITY = 5;
const SMALL_ARR = 200_000;
const STALE_VALUE_ARR = 100_000;
const HEALTHY_DENSITY = 2;
const INSIGHT_LIMIT = 5;

export function deriveAccountInsights(bubbles: AccountBubble[]): AccountInsights {
  const withName = bubbles.filter((b) => b.accountName);

  const highArrLowInfluence = withName
    .filter((b) => b.totalArr >= WHALE_ARR && b.influenceDensity < LOW_INFLUENCE)
    .slice(0, INSIGHT_LIMIT)
    .map((b) => ({ name: b.accountName, arr: b.totalArr, density: b.influenceDensity }));

  const overInvested = withName
    .filter((b) => b.influenceDensity >= OVER_INVESTED_DENSITY && b.totalArr < SMALL_ARR)
    .slice(0, INSIGHT_LIMIT)
    .map((b) => ({ name: b.accountName, arr: b.totalArr, density: b.influenceDensity }));

  const staleHighValue = withName
    .filter((b) => b.freshness === 'stale' && b.totalArr >= STALE_VALUE_ARR)
    .slice(0, INSIGHT_LIMIT)
    .map((b) => ({ name: b.accountName, arr: b.totalArr, days: b.bestDaysSince ?? undefined }));

  const healthyCount = withName.filter(
    (b) => b.freshness === 'active' && b.influenceDensity >= HEALTHY_DENSITY,
  ).length;

  return {
    totalAccounts: withName.length,
    highArrLowInfluence,
    overInvested,
    staleHighValue,
    healthyCount,
  };
}
